/**
 * ProgressRing - Circular progress indicator for running tools.
 *
 * Shows a spinning/pulsing ring with configurable color based on status.
 */

interface ProgressRingProps {
  size?: number
  strokeWidth?: number
  status: 'pending' | 'running' | 'success' | 'error'
  className?: string
}

export function ProgressRing({
  size = 16,
  strokeWidth = 2,
  status,
  className = '',
}: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI

  const statusColors = {
    pending: 'stroke-amber-400',
    running: 'stroke-blue-400',
    success: 'stroke-emerald-400',
    error: 'stroke-red-400',
  }

  const isAnimating = status === 'pending' || status === 'running'

  return (
    <svg
      width={size}
      height={size}
      className={`${isAnimating ? 'animate-spin' : ''} ${className}`}
      style={{ animationDuration: '1.5s' }}
    >
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        className="stroke-white/10"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        className={statusColors[status]}
        strokeDasharray={circumference}
        strokeDashoffset={status === 'running' ? circumference * 0.25 : 0}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  )
}

export default ProgressRing
